import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { App as AntdApp, ConfigProvider } from 'antd';

const STORAGE_KEY = 'pingTheme';

// Registry of available visual themes. Each entry pairs the CSS data-theme key
// (used by index.css / themes.css variable scopes) with an Ant Design token set
// so the component library stays visually in sync with the CSS variables.
export const THEMES = {
  default: {
    key: 'default',
    label: 'Classic',
    antd: {
      token: {
        colorPrimary: '#57150B',
        colorInfo: '#57150B',
        colorSuccess: '#2e7d32',
        colorWarning: '#FFCC00',
        colorError: '#c62828',
        borderRadius: 8,
        fontFamily: "'Outfit', sans-serif",
      },
      components: {
        Button: { controlHeight: 38, fontWeight: 700 },
        Card: { borderRadiusLG: 12 },
      },
    },
  },
  sketchbook: {
    key: 'sketchbook',
    label: 'Sketchbook',
    antd: {
      token: {
        colorPrimary: '#2f6fb3',
        colorInfo: '#2f6fb3',
        colorSuccess: '#4a9d5b',
        colorWarning: '#f2b705',
        colorError: '#e8623b',
        colorTextBase: '#3a3327',
        borderRadius: 14,
        fontFamily: "'Patrick Hand', 'Outfit', sans-serif",
        fontSize: 15,
      },
      components: {
        Button: { controlHeight: 40, fontWeight: 700, borderRadius: 16 },
        Card: { borderRadiusLG: 18 },
      },
    },
  },
};

const DEFAULT_THEME = 'default';

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  themes: THEMES,
  setTheme: () => {},
  toggleTheme: () => {},
});

const readStoredTheme = () => {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && THEMES[stored]) return stored;
  } catch (e) {
    // localStorage may be unavailable (privacy mode) — fall back to default
  }
  return DEFAULT_THEME;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredTheme);

  // Reflect the active theme onto <html> so CSS variable scopes apply, and
  // persist the choice for the next visit.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      // ignore persistence failures
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState((prev) => (THEMES[next] ? next : prev));
  }, []);

  const toggleTheme = useCallback(() => {
    const order = Object.keys(THEMES);
    setThemeState((prev) => {
      const idx = order.indexOf(prev);
      return order[(idx + 1) % order.length];
    });
  }, []);

  const value = useMemo(
    () => ({ theme, themes: THEMES, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  const antdTheme = (THEMES[theme] || THEMES[DEFAULT_THEME]).antd;

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdTheme}>
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
